import { useEffect, useMemo, useRef, useState } from "react";
import { estimateSubscription } from "../api/api";
import {
  buildEstimateRequestSignature,
  buildSubscriptionEstimateItems,
  buildSubscriptionEstimatePayload,
} from "../utils/estimateRequestBuilder";

// Providers that need a country before the first estimate is useful.
const COUNTRY_REQUIRED_ESTIMATE_PROVIDERS = new Set(["frisbii"]);

/**
 * Module-level caches so a skin remount does not re-POST the same cart
 * signature (per-hook refs were lost on unmount and caused duplicate estimates).
 */
const estimateCompletedBySignature = new Map();
const estimateInFlightBySignature = new Map();

const isAbortError = (error) =>
  error?.name === "CanceledError" ||
  error?.name === "AbortError" ||
  error?.code === "ERR_CANCELED";

/**
 * Fetches billing estimate once per unique cart + country + coupon set.
 * Shares in-flight / completed results across remounts of the same checkout.
 */
export function useSubscriptionEstimate({
  billingConnectionId,
  planId,
  platformItemDetails,
  platformItem,
  selectedAddons,
  formData,
  clientCountry,
  effectiveCouponCodes,
  setEstimatedOrder,
  /** Checkout/session currency (e.g. hosted page BRL) — overrides catalog default on multi-currency prices. */
  checkoutCurrency,
  /** Merchant-forced trial length from session create (days). */
  forceTrialPeriodDays,
  /** When true, wait until billing country is BR before estimating (BRL checkout). */
  requireBrazilForBrl = true,
  /** Billing platform name — used to gate country-required providers (e.g. frisbii). */
  billingPlatformName,
}) {
  const [estimatedOrder, setLocalEstimatedOrder] = useState(null);
  const [isEstimating, setIsEstimating] = useState(false);
  const requestSignatureRef = useRef(null);

  const currency = checkoutCurrency || platformItem?.currencyCode;
  const isBrl = currency === "BRL";

  const addonSelectionKey = useMemo(
    () =>
      JSON.stringify(
        (selectedAddons || []).map((addon) => ({
          id: addon.itemPriceId,
          q: addon.quantity ?? 1,
        }))
      ),
    [selectedAddons]
  );

  const subscriptionItems = useMemo(
    () =>
      buildSubscriptionEstimateItems({
        planId,
        platformItem,
        platformItemDetails,
        selectedAddons,
      }),
    [planId, platformItem, platformItemDetails, addonSelectionKey, selectedAddons]
  );

  const resolvedCountry = formData?.billingAddress?.country || clientCountry || null;

  const providerRequiresCountry = COUNTRY_REQUIRED_ESTIMATE_PROVIDERS.has(
    String(
      billingPlatformName ||
        platformItem?.billingPlatformName ||
        platformItemDetails?.[0]?.billingPlatformName ||
        ""
    )
      .trim()
      .toLowerCase()
  );

  const requestSignature = useMemo(() => {
    if (!billingConnectionId || !planId || !platformItemDetails?.length || !currency) {
      return null;
    }
    // Wait for geo / billing country before estimating for providers that need it.
    if (providerRequiresCountry && !resolvedCountry) {
      return null;
    }
    if (requireBrazilForBrl && isBrl && resolvedCountry !== "BR") {
      return null;
    }
    return buildEstimateRequestSignature({
      billingConnectionId,
      planId,
      currency,
      country: resolvedCountry,
      couponCodes: effectiveCouponCodes,
      subscriptionItems,
      forceTrialPeriodDays,
    });
  }, [
    billingConnectionId,
    planId,
    platformItemDetails,
    currency,
    requireBrazilForBrl,
    isBrl,
    providerRequiresCountry,
    resolvedCountry,
    effectiveCouponCodes,
    subscriptionItems,
    forceTrialPeriodDays,
  ]);

  requestSignatureRef.current = requestSignature;

  useEffect(() => {
    if (!requestSignature) return;

    let cancelled = false;

    const applyEstimate = (estimate) => {
      if (cancelled) return;
      setLocalEstimatedOrder(estimate);
      setEstimatedOrder(estimate);
      setIsEstimating(false);
    };

    const cached = estimateCompletedBySignature.get(requestSignature);
    if (cached !== undefined) {
      applyEstimate(cached);
      return;
    }

    setIsEstimating(true);

    let entry = estimateInFlightBySignature.get(requestSignature);
    if (!entry) {
      // Only clear when starting a brand-new request (not when joining in-flight).
      setLocalEstimatedOrder(null);
      setEstimatedOrder(null);

      const payload = buildSubscriptionEstimatePayload({
        currency,
        subscriptionItems,
        country: resolvedCountry,
        couponCodes: effectiveCouponCodes,
        forceTrialPeriodDays,
      });

      const promise = estimateSubscription(billingConnectionId, payload)
        .then((estimate) => {
          estimateCompletedBySignature.set(requestSignature, estimate);
          estimateInFlightBySignature.delete(requestSignature);
          return estimate;
        })
        .catch((error) => {
          estimateInFlightBySignature.delete(requestSignature);
          throw error;
        });

      entry = { promise };
      estimateInFlightBySignature.set(requestSignature, entry);
    }

    entry.promise
      .then((estimate) => {
        applyEstimate(estimate);
      })
      .catch((error) => {
        if (cancelled || isAbortError(error)) return;
        console.error("Error estimating subscription:", error);
        setLocalEstimatedOrder(null);
        setEstimatedOrder(null);
        setIsEstimating(false);
      });

    return () => {
      // Do not abort the shared in-flight POST on unmount — a remount will
      // reuse the same promise instead of hitting the server again.
      cancelled = true;
    };
  }, [
    requestSignature,
    billingConnectionId,
    currency,
    subscriptionItems,
    resolvedCountry,
    effectiveCouponCodes,
    setEstimatedOrder,
    forceTrialPeriodDays,
  ]);

  const forceRefresh = () => {
    const signature = requestSignatureRef.current;
    if (signature) {
      estimateCompletedBySignature.delete(signature);
      estimateInFlightBySignature.delete(signature);
    }
  };

  return { estimatedOrder, isEstimating, forceRefresh };
}
